import { toast } from '@heroui/react'
import { create } from 'zustand'

import { uploadsApi } from '@/api/uploads.api'
import i18n from '@/i18n'
import type { FileUpload, GetUploadsParams } from '@/types/uploads'

interface UploadsStore {
  uploads: FileUpload[]
  isLoading: boolean
  error: string | null

  filters: {
    filename: string
  }

  pagination: {
    page: number
    limit: number
    totalPages: number
    total: number
  }

  setFilters: (filters: Partial<UploadsStore['filters']>) => void
  setPagination: (pagination: Partial<UploadsStore['pagination']>) => void
  fetchUploads: () => Promise<void>
}

export const useUploadsStore = create<UploadsStore>((set, get) => ({
  uploads: [],
  isLoading: false,
  error: null,

  filters: {
    filename: '',
  },

  pagination: {
    page: 0,
    limit: 20,
    totalPages: 0,
    total: 0,
  },

  setFilters: newFilters => {
    set(state => ({
      filters: { ...state.filters, ...newFilters },
      pagination: { ...state.pagination, page: 0 },
    }))
    get().fetchUploads()
  },

  setPagination: newPagination => {
    set(state => ({
      pagination: { ...state.pagination, ...newPagination },
    }))
    get().fetchUploads()
  },

  fetchUploads: async () => {
    const { filters, pagination } = get()
    set({ isLoading: true, error: null })

    const params: GetUploadsParams = {
      filename: filters.filename || undefined,
      page: pagination.page,
      limit: pagination.limit,
    }

    try {
      const response = await uploadsApi.getUploads(params)
      const total = response.Pagination?.Total || 0
      set({
        uploads: response.Uploads || [],
        pagination: {
          ...pagination,
          total,
          totalPages: Math.ceil(total / pagination.limit),
        },
      })
    } catch (err) {
      const message = (err as Error).message
      set({ error: message, uploads: [] })
      toast.danger(
        i18n.t('uploads.toast.fetch_error', 'Не удалось загрузить историю загрузок'),
        { description: message }
      )
    } finally {
      set({ isLoading: false })
    }
  },
}))
